import React from "react";
import { render, unmountComponentAtNode } from "react-dom";

import { createInitFrameWithDataSourceAction, PARTICIPANT_TYPE_ATTRIBUTE_RENDERER, ParticipantsAttributes, ROLE_USERS_ATTRIBUTE_RENDERER, WFParticipantsDialog } from "@multiweb/frames";
import { bridge } from "@multiweb/reactCore/bridge";
import { createDeleteDataSourceAction, createInitDataSourceAction, createSetDatasourceMultiselectModeAction, IDataSourceAttribute, IDataSourceInitConfig, LOAD_DATA, REFRESH_DATA } from "@multiweb/reactCore/datasource";
import { DATA_SOURCE_INIT_STATE } from "@multiweb/reactCore/datasource/initState";
import { CUSTOM_DS_WF_PARTICIPANTS, ParticipantCustomMethodDataType } from "@multiweb/reactCore/workflow";
import { createRootComponent } from "@multiweb/reactSetup";
import { createDataSourceAttribute } from "@multiweb/reactUtils";
import { IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { IOWObjectList, IParticipant } from "@multiweb/types/multiweb";
import { AppContext } from "App/AppContext";
import { IDisposable } from "Global/Base/IDisposable";
import { Deferred } from "Global/Base/Promises/PromiseUtils";
import { MODAL_ROOT_ID } from "UI/UI.React/constants";

const { dispatchAction } = bridge;

export class WFDialogConnector implements IDisposable {
    private _modalRoot: HTMLElement;
    private _isInitialized: boolean = false;
    private _instanceId: string;

    private readonly _dataSourceKey: string = "";
    private readonly _deferredPromise: Deferred<IParticipant[]>;
    private _chooseFromUsersDefault: boolean;
    private _objectList: IOWObjectList;

    constructor(deferredPromise: Deferred<IParticipant[]>, instanceId: string, objectList: IOWObjectList, activityId: string, chooseFromUsersDefault: boolean) {
        this._modalRoot = document.getElementById(MODAL_ROOT_ID);
        this._deferredPromise = deferredPromise;
        this._dataSourceKey = activityId;
        this._instanceId = instanceId;
        this._chooseFromUsersDefault = chooseFromUsersDefault;
        this._objectList = objectList;
    }

    public initializeVisual(): void {
        if (this._isInitialized) {
            return;
        }
        this._isInitialized = true;

        const developmentMode = !!AppContext.instance.configuration.developmentMode;
        const RootComponent = createRootComponent(
            developmentMode,
        );

        this._setUpDataSource();

        render(
            <RootComponent>
                <WFParticipantsDialog dialogCompletedPromise={this._deferredPromise} dataSourceKey={this._dataSourceKey} chooseFromUsersDefault={this._chooseFromUsersDefault} />
            </RootComponent>
            , this._modalRoot);
    }

    private _setUpDataSource(): void {
        const objectName = "WFDialog";

        const attributes: IDataSourceAttribute[] = [
            createDataSourceAttribute(ParticipantsAttributes.personName),
            createDataSourceAttribute(ParticipantsAttributes.name),
            createDataSourceAttribute(ParticipantsAttributes.actualRoleName),
            createDataSourceAttribute(ParticipantsAttributes.participantType, { attributeRendererKey: PARTICIPANT_TYPE_ATTRIBUTE_RENDERER }),
            createDataSourceAttribute(ParticipantsAttributes.roleUsers, { attributeRendererKey: ROLE_USERS_ATTRIBUTE_RENDERER }),
        ];

        const additionalDSData: ParticipantCustomMethodDataType = {} as ParticipantCustomMethodDataType;

        additionalDSData[LOAD_DATA] =
            additionalDSData[REFRESH_DATA] = {
                data: [
                    this._instanceId,
                    this._objectList,
                    this._dataSourceKey,
                ],
            };

        const config: IDataSourceInitConfig = {
            objectName,
            attributes,
            recordIdKey: "id",
            formContext: DATA_SOURCE_INIT_STATE.config.formContext,
            isPersistent: false,
            customDataSourceId: CUSTOM_DS_WF_PARTICIPANTS,
            customMethodsData: additionalDSData,
            hasWorkflow: false,
        };

        dispatchAction(
            createInitFrameWithDataSourceAction(
                this._dataSourceKey,
                false,
                true,
                config,
                false,
                {},
                [],
                {},
            ),
        );

        const attributesInfo: ReadonlyArray<IPublicAttributeInfo> = [{
            attributeName: ParticipantsAttributes.name,
            type: OWDataType.string,
        },
        {
            attributeName: ParticipantsAttributes.personName,
            type: OWDataType.string,
        },
        {
            attributeName: ParticipantsAttributes.actualRoleName,
            type: OWDataType.string,
        },
        {
            attributeName: ParticipantsAttributes.participantType,
            type: OWDataType.enum,
        },
        {
            attributeName: ParticipantsAttributes.roleUsers,
            type: OWDataType.OWObjectList,
        }];

        dispatchAction(createInitDataSourceAction(this._dataSourceKey, config, false, undefined, undefined, attributesInfo));
        dispatchAction(createSetDatasourceMultiselectModeAction(this._dataSourceKey, true));
    }

    public dispose(): void {
        unmountComponentAtNode(this._modalRoot);
        dispatchAction(createDeleteDataSourceAction(this._dataSourceKey));
    }
}
